import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { formatApiErrorDetail, getPublicFileUrl } from "@/lib/api";
import { PublicLangProvider, usePublicLang } from "@/contexts/PublicLangContext";
import { applyLandingSeo, applyStructuredData } from "@/lib/seo";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingHero from "@/components/landing/LandingHero";
import TrustBar from "@/components/landing/TrustBar";
import ServiceLines from "@/components/landing/ServiceLines";
import StatsSection from "@/components/landing/StatsSection";
import PromosSection from "@/components/landing/PromosSection";
import FeaturedBookingSection from "@/components/landing/FeaturedBookingSection";
import IntroSection from "@/components/landing/IntroSection";
import TreatmentsGallery from "@/components/landing/TreatmentsGallery";
import DoctorSection from "@/components/landing/DoctorSection";
import TeamSection from "@/components/landing/TeamSection";
import ResultsGallery from "@/components/landing/ResultsGallery";
import TestimonialsSection from "@/components/landing/TestimonialsSection";
import NewsSection from "@/components/landing/NewsSection";
import FaqSection from "@/components/landing/FaqSection";
import VisitSection from "@/components/landing/VisitSection";
import ClosingCta from "@/components/landing/ClosingCta";
import LandingFooter from "@/components/landing/LandingFooter";
import FinancingBanner from "@/components/landing/FinancingBanner";
import PublicWhatsAppButton from "@/components/publicBooking/PublicWhatsAppButton";
import { LANDING } from "@/constants/testIds";

// La plataforma sirve una unica clinica real desde la raiz publica del
// sitio (mismo patron que otros textos de marca ya fijos en el codebase).
const DEFAULT_SLUG = "strani-just-because";

function LandingContent({ data }) {
  const { lang } = usePublicLang();
  const navigate = useNavigate();
  const { clinic, landing, doctor, team = [], posts = [], services, featured_services, testimonials, gallery, faqs } = data;

  // Fix A (2026-09-04): paleta de marca aprobada por la clinica. Los roles
  // (accion = Tiffany, decorativo = oro, base = marfil, oscuro = espresso)
  // salen del manual de marca; no reasignar sin consultar.
  const brandStyle = {
    "--brand-primary": clinic.brand_primary_color || "#B99A62",   // Oro suave
    "--brand-secondary": clinic.brand_secondary_color || "#F7F3ED", // Marfil
    "--brand-accent": "#81D8D0",     // Tiffany - color de accion (CTAs)
    "--brand-dark": "#292522",       // Espresso - texto y secciones oscuras
    "--brand-ivory": "#F7F3ED",      // Marfil - fondo base
    "--brand-champagne": "#D8C3A5",
    "--brand-taupe": "#A89582",
    "--brand-silver": "#C0C2C4",
    "--brand-gold": "#D4AF37",       // Oro clasico - realces
  };

  useEffect(() => {
    const title = landing.meta_title || `${clinic.name} · Medicina Estética Houston`;
    const description = landing.meta_description || landing.intro_text || landing.hero_subtitle || clinic.name;
    applyLandingSeo({ title, description, ogImage: landing.hero_image_url ? getPublicFileUrl(landing.hero_image_url) : undefined, url: window.location.href });
    applyStructuredData({
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      name: clinic.name,
      image: landing.hero_image_url ? getPublicFileUrl(landing.hero_image_url) : undefined,
      address: clinic.address ? { "@type": "PostalAddress", streetAddress: clinic.address } : undefined,
      telephone: clinic.phone || undefined,
      email: clinic.email || undefined,
      url: window.location.href,
    });
  }, [clinic, landing]);

  const goToBooking = (serviceId) => {
    navigate(`/reservas/${clinic.slug}`, { state: serviceId ? { preselectedServiceId: serviceId, lang } : { lang } });
  };

  const sectionsAvailable = {
    doctor: Boolean(doctor && (doctor.photo_url || doctor.bio || doctor.specialty)),
    team: team.length > 0,  // Fix AZ (2026-10-30)
    results: gallery.length > 0,
    faq: faqs.length > 0,
  };

  return (
    <div className="min-h-screen bg-[#F7F3ED] font-sans text-[#292522]" style={brandStyle} data-testid={LANDING.page}>
      <LandingHeader clinic={clinic} onBook={() => goToBooking()} sectionsAvailable={sectionsAvailable} />
      <LandingHero landing={landing} onBook={() => goToBooking()} />
      <TrustBar />
      <ServiceLines />
      <FeaturedBookingSection services={featured_services} onBook={(id) => goToBooking(id)} />
      <IntroSection landing={landing} />
      <StatsSection />
      <PromosSection services={services} onBookService={(s) => goToBooking(s.id)} />
      <TreatmentsGallery services={services} gallery={gallery} onBookService={(s) => goToBooking(s.id)} />
      <FinancingBanner clinic={clinic} />
      <DoctorSection doctor={doctor} />
      <TeamSection team={team} clinic={clinic} />
      <ResultsGallery gallery={gallery} services={services} />
      <TestimonialsSection testimonials={testimonials} />
      {/* Fix BD (2026-10-30): novedades de redes */}
      <NewsSection posts={posts} clinic={clinic} />
      <FaqSection faqs={faqs} />
      <VisitSection clinic={clinic} landing={landing} />
      <ClosingCta onBook={() => goToBooking()} />
      <LandingFooter clinic={clinic} />
      <PublicWhatsAppButton clinic={clinic} />
    </div>
  );
}

export default function LandingPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    api.get(`/public/clinics/${DEFAULT_SLUG}/landing`)
      .then(({ data }) => { if (active) setData(data); })
      .catch((err) => { if (active) setError(formatApiErrorDetail(err.response?.data?.detail)); });
    return () => { active = false; };
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F3ED] px-4" data-testid={LANDING.error}>
        <p className="text-[#6B6058] text-center max-w-sm">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F3ED]" data-testid={LANDING.loading}>
        <Loader2 className="w-8 h-8 animate-spin text-[#B99A62]" />
      </div>
    );
  }

  const defaultLang = data.clinic.languages?.includes("es") ? "es" : "en";
  return (
    <PublicLangProvider defaultLang={defaultLang}>
      <LandingContent data={data} />
    </PublicLangProvider>
  );
}
